// React
import { useEffect, useMemo, useRef, useState } from "react";

// Redux
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

// Interfaces
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";

// Context
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Hooks
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

// API
import { onCreatePayRate, onUpdatePto, onUpdatePtoBalance, onUpdateTimeCard, onUpdateWorkedHours } from "@DataPages/api/timeAndPayRates";

// Components
import TimeAndPayRatesBody from "@/DataPages/components/TimeAndPayRates/TimeAndPayRatesBody";
import BodyLayer from "@Layouts/components/Layers/BodyLayer";
import PayPeriodSideBar from "@Layouts/components/SideBars/PayPeriodSideBar";

const QUERY_IDENTIFIERS = ["timecard", "workedHours", "payRates", "personalTimeOff", "personalTimeOffBalance"];
const checkIsUpdatable = (commissionCycle: IPeriodTreeSelection) => {
	if (!commissionCycle?.startDate) {
		return false;
	}
	return (
		Boolean(commissionCycle?.cycleStatus === "CLOSING" || commissionCycle?.cycleStatus === "ACTIVE") &&
		new Date(commissionCycle?.startDate) <= new Date()
	);
};
export default function TimeAndPayRatesLayer() {
	const { clearCache } = useCacheCleaner();
	const lastSelected = useRef<IPeriodTreeSelection>(null);
	const [selection, setSelection] = useState<IPeriodTreeSelection>(null);

	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId ?? "";

	const { addSubscription, clearSubscriptionContext } = useSubscriptions();

	const sideBar = useMemo(() => {
		const sideSelection = function (selected: IPeriodTreeSelection, isActiveCycle = false) {
			if (selected?._id !== lastSelected.current?._id) {
				lastSelected.current = selected;
				setSelection(selected);

				if (isActiveCycle && location) {
					addSubscription(
						["timeAndPayRates", selected?.payPeriodId],
						[
							{
								context: "onUpdateTimecard",
								query: onUpdateTimeCard,
								variables: {
									locationId,
									payPeriodId: selected?.payPeriodId,
								},
							},
							{
								context: "onUpdateWorkedHours",
								query: onUpdateWorkedHours,
								variables: {
									commissionCycleId: selected?.cycleId,
									payPeriodId: selected?.payPeriodId,
								},
							},
							{
								context: "onCreatePayRate",
								query: onCreatePayRate,
								variables: {
									locationId,
									commissionCycleId: selected?.cycleId,
									payPeriodId: selected?.payPeriodId,
								},
							},
							{
								context: "onUpdatePto",
								query: onUpdatePto,
								variables: {
									locationId,
									commissionCycleId: selected?.cycleId,
									payPeriodId: selected?.payPeriodId,
								},
							},
							{
								context: "onUpdatePtoBalance",
								query: onUpdatePtoBalance,
								variables: {
									locationId,
									commissionCycleId: selected?.cycleId,
									payPeriodId: selected?.payPeriodId,
								},
							},
						]
					);
				}
			}
		};

		return <PayPeriodSideBar callbackSelected={sideSelection} selectDefaultLastCommissionCycle />;
	}, [addSubscription, locationId]);

	useEffect(() => {
		return () => {
			setSelection(null);
			clearCache(QUERY_IDENTIFIERS);
			clearSubscriptionContext();
			lastSelected.current = null;
		};
	}, [clearSubscriptionContext, locationId]);
	return (
		<BodyLayer fixedContent={sideBar}>
			<TimeAndPayRatesBody selected={selection} isUpdatable={checkIsUpdatable(selection)} />
		</BodyLayer>
	);
}
